import { EventOrgTypeFilter, OrderEvent } from 'models/order-event';
import { Organization } from 'models/organization';

/**
 * The organization behind an order event, or undefined when the event has no
 * user. Writes made through the public API (DD-3579, DD-3688) are stamped with
 * an API caller that has no user record, so their `user` comes back null.
 */
export const eventOrganization = (item: OrderEvent): Organization | undefined => {
    const organization = item.user?.organization;
    return organization && typeof organization === 'object' ? (organization as Organization) : undefined;
};

/**
 * Only labs use the public API, so an event without a user is a lab event.
 */
export const eventOrganizationType = (item: OrderEvent): string =>
    eventOrganization(item)?.type ?? (item.user ? '' : 'lab');

/**
 * The organization named in an event's title: the user's organization, the
 * user's own name when it did not load (DD-3794), or `apiIntegrationLabel` for
 * an event without a user.
 */
export const eventOrganizationName = (item: OrderEvent, apiIntegrationLabel: string): string =>
    eventOrganization(item)?.name ?? item.user?.fullName ?? apiIntegrationLabel;

/**
 * Who made the event, for its title: "Name - Organization", only the name when
 * the user's organization did not load (DD-3794), or `apiIntegrationLabel` for
 * an event without a user.
 */
export const eventAuthor = (item: OrderEvent, apiIntegrationLabel: string): string => {
    if (!item.user) {
        return apiIntegrationLabel;
    }
    const organizationName = eventOrganization(item)?.name;
    return organizationName ? `${item.user.fullName} - ${organizationName}` : item.user.fullName;
};

/**
 * DD-3794: an event whose side is unknown (its user's organization did not
 * load) is shown while both sides are, and left out once the viewer narrows the
 * timeline to clinic or lab events.
 */
export const matchesOrganizationTypeFilter = (item: OrderEvent, filter: EventOrgTypeFilter): boolean => {
    switch (eventOrganizationType(item)) {
        case 'clinic':
            return filter.showClinicEvents;
        case 'lab':
            return filter.showLabEvents;
        default:
            return filter.showClinicEvents && filter.showLabEvents;
    }
};
